import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, ExternalLink, Loader2, HardDrive, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Google Drive admin page · 2026-03-01.
 *
 * Shows connection status + Connect / Disconnect buttons.
 * Admin only (route-level gate matches other admin pages).
 */
export default function GoogleDrivePage() {
  const { api, user } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/google-drive/status');
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false, error: e?.response?.data?.detail || 'Unknown error' });
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Show toast if we were redirected back from Google
  useEffect(() => {
    if (params.get('connected') === 'true') {
      toast.success('Google Drive connected successfully!');
    } else if (params.get('error')) {
      const err = params.get('error');
      toast.error(`Connection failed: ${err}`);
    }
  }, [params]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/google-drive/connect');
      if (data.authorization_url) {
        // Redirect THIS tab to Google so cookies survive the callback
        window.location.href = data.authorization_url;
      } else {
        toast.error('No authorization URL returned.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not initiate Google sign-in.');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Drive? CRM saves will stop pushing to Drive. Browser downloads continue as normal.')) return;
    setDisconnecting(true);
    try {
      await api.post('/google-drive/disconnect');
      toast.success('Google Drive disconnected.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not disconnect.');
    }
    setDisconnecting(false);
  };

  const isAdmin = user?.role === 'admin';
  const isConnected = status?.connected === true;

  return (
    <div data-testid="google-drive-page" className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope] flex items-center gap-2">
          <HardDrive size={28} className="text-[#D35400]" /> Google Drive
        </h1>
        <p className="text-sm text-slate-500 mt-1 font-medium">
          Every PDF saved from the CRM is automatically pushed to your Google Drive — no per-PC browser setup needed.
        </p>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-6 text-center text-slate-400">
          <Loader2 size={24} className="mx-auto animate-spin mb-2" /> Loading connection status…
        </div>
      ) : isConnected ? (
        <div data-testid="gd-connected-panel" className="bg-emerald-50 border border-emerald-200 rounded-md p-6 space-y-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={24} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg font-bold text-emerald-900">Connected</h2>
              <p className="text-sm text-emerald-800/80 mt-1">
                Account: <span className="font-mono font-semibold">{status?.email || '(unknown)'}</span>
              </p>
              {status?.connected_at && (
                <p className="text-xs text-emerald-700/60 mt-1">
                  Connected on {new Date(status.connected_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          <div className="bg-white border border-emerald-200 rounded-md p-4 space-y-2">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Target folder</p>
            <p className="text-sm text-slate-700">
              <code className="bg-slate-100 px-2 py-1 rounded font-mono text-xs">
                My Drive → Customers → A{'{appraisal_no}'} {'{client_surname}'} → *.pdf
              </code>
            </p>
            <p className="text-xs text-slate-500">
              Per-client subfolders auto-created on first save. The CRM can only touch files it creates (minimum-permission <code>drive.file</code> scope).
            </p>
          </div>

          {isAdmin && (
            <div className="flex gap-2">
              <button
                data-testid="gd-disconnect-button"
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-md text-sm font-semibold hover:bg-red-50 transition-colors"
              >
                {disconnecting ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                Disconnect
              </button>
              <a
                href={`https://drive.google.com/drive/folders/${status?.customers_folder_id || ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 border border-[#E2E2DF] rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <ExternalLink size={14} /> Open Drive folder
              </a>
            </div>
          )}
        </div>
      ) : (
        <div data-testid="gd-disconnected-panel" className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900">Not connected</h2>
              <p className="text-sm text-slate-600 mt-1">
                CRM saves currently land in your browser's download folder on your PC. Connect Google Drive to push them to a shared cloud folder automatically.
              </p>
            </div>
          </div>

          {status?.error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
              {status.error}
            </div>
          )}

          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-1">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">What happens when you connect</p>
            <ul className="text-sm text-slate-700 space-y-1 list-disc ml-5 mt-2">
              <li>You sign in to Google and click <strong>Allow</strong></li>
              <li>CRM creates a <code className="bg-white px-1 py-0.5 rounded text-xs">Customers</code> folder in your Drive</li>
              <li>Every CRM save pushes a copy to <code className="bg-white px-1 py-0.5 rounded text-xs">Customers/A6903 Smith/</code> (per-client subfolders auto-created)</li>
              <li>The CRM can <strong>only</strong> touch files it creates — your other Drive files are untouched</li>
            </ul>
          </div>

          {isAdmin ? (
            <button
              data-testid="gd-connect-button"
              onClick={handleConnect}
              disabled={connecting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#D35400] hover:bg-[#A04000] disabled:bg-slate-300 text-white rounded-md text-sm font-semibold transition-all duration-200 shadow-sm"
            >
              {connecting ? <Loader2 size={16} className="animate-spin" /> : <HardDrive size={16} />}
              {connecting ? 'Redirecting to Google…' : 'Connect Google Drive'}
            </button>
          ) : (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 rounded">
              Only an admin can connect Google Drive for the whole CRM. Ask Trevor to connect it once — then every operator's saves land in the same shared folder.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
